import React, { useState, useEffect } from 'react'
import { MessageSquare } from 'lucide-react'
import { getFullApiUrl } from '../../config/api'

interface Feedback {
  feedbackId: string
  timestamp: string
  feedbackType: string
  message: string
  userEmail: string
  source: string
  page: string
  userTier: string
  country?: string
}

export const FeedbackViewer: React.FC = () => {
  const [feedback, setFeedback] = useState<Feedback[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState({ type: 'all', source: 'all' })

  useEffect(() => {
    fetchFeedback()
  }, [])

  const fetchFeedback = async () => {
    try {
      const token = localStorage.getItem('authToken')
      const response = await fetch(getFullApiUrl(`/admin/feedback`), {
        headers: { Authorization: `Bearer ${token}` },
      })
      const data = await response.json()
      setFeedback(data.feedback || [])
    } catch (error) {
      console.error('Error fetching feedback:', error)
    } finally {
      setLoading(false)
    }
  }

  const filtered = feedback.filter(f => 
    (filter.type === 'all' || f.feedbackType === filter.type) &&
    (filter.source === 'all' || f.source === filter.source)
  )

  if (loading) return <div className="text-center py-8">Loading feedback...</div>

  return (
    <div>
      <div className="flex gap-4 mb-6">
        <select
          value={filter.type}
          onChange={(e) => setFilter({ ...filter, type: e.target.value })}
          className="border rounded px-3 py-2"
        >
          <option value="all">All Types</option>
          <option value="bug">Bug</option>
          <option value="feature">Feature</option>
          <option value="general">General</option>
          <option value="other">Other</option>
        </select>
        <select
          value={filter.source}
          onChange={(e) => setFilter({ ...filter, source: e.target.value })}
          className="border rounded px-3 py-2"
        >
          <option value="all">All Sources</option>
          <option value="marketplace">Marketplace</option>
          <option value="faiss">FAISS</option>
        </select>
        <div className="ml-auto text-gray-600">
          {filtered.length} of {feedback.length} items
        </div>
      </div>

      <div className="space-y-4">
        {filtered.map((f) => (
          <div key={f.feedbackId} className="border rounded-lg p-4 bg-white shadow-sm">
            <div className="flex items-start gap-3">
              <MessageSquare className="text-blue-600 mt-1" size={20} />
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                    {f.feedbackType}
                  </span>
                  <span className="px-2 py-1 bg-gray-100 text-gray-800 text-xs rounded">
                    {f.source}
                  </span>
                  <span className="text-xs text-gray-500">
                    {new Date(f.timestamp).toLocaleString()}
                  </span>
                </div>
                <p className="text-gray-800 mb-2">{f.message}</p>
                <div className="text-sm text-gray-600">
                  <span className="font-medium">Email:</span> {f.userEmail} | 
                  <span className="font-medium"> Tier:</span> {f.userTier} | 
                  <span className="font-medium"> Country:</span> {f.country || 'Unknown'} | 
                  <span className="font-medium"> Page:</span> {f.page}
                </div>
              </div>
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="text-center py-8 text-gray-500">No feedback found</div>
        )}
      </div>
    </div>
  )
}
